import {Autocomplete, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Step, StepLabel, Stepper, Table, TableBody, TableCell, TableRow, TextField} from "@mui/material";
import React, {forwardRef, useEffect, useImperativeHandle, useState} from "react";
import projectService from "../../services/ProjectService";
import {Assignment, Close, FormatListNumbered, LocalShipping, Person, Upload} from "@mui/icons-material";
import projectTaskService from "../../services/ProjectTaskService";
import {useSnackbar} from "../../util/SnackbarProvider";
import VisuallyHiddenInput from "../common/VisuallyHiddenInput";
import {uploadFiles} from "../../services/FileService";
import userService from "../../services/UserService";
import vehicleService from "../../services/VehicleService";
import taskService from "../../services/TaskService";

const steps = ['Cél', 'Kiválasztás', 'Fájlok'];

const targetOptionStyle = {fontSize: "calc(8vw - 0px)", margin: "0px"}
const targetOptions = [
    {icon: <Assignment sx={targetOptionStyle}/>, title: "Projekt", id: "project"},
    {icon: <FormatListNumbered sx={targetOptionStyle}/>, title: "Feladat", id: "task"},
    {icon: <LocalShipping sx={targetOptionStyle}/>, title: "Jármű", id: "vehicle"},
    {icon: <Person sx={targetOptionStyle}/>, title: "Felhasználó", id: "user"},
];

const GeneralFileUploadDialog = forwardRef((_, ref) => {

    const [open, setOpen] = useState(false)
    const [formValue, setFormValue] = useState(JSON.parse(localStorage.getItem("general-file-upload-form-values") || '{}'))
    const [files, setFiles] = useState([])
    const [activeStep, setActiveStep] = useState(0)
    const [targetOption, setTargetOption] = useState()
    const {sendAlertMessage} = useSnackbar()
    const [waitingForServer, setWaitingForServer] = useState(false)

    useImperativeHandle(ref, () => ({
        openDialog: () => {
            setOpen(true);
        },
    }));

    const [projects, setProjects] = useState(null);
    useEffect(() => {
        if (projects != null || !['project', 'task', 'user'].includes(targetOption)) return
        projectService.getSelectors()
            .then(response => setProjects(response))
            .catch(error => console.error(error))
    }, [projects, targetOption]);

    const [vehicles, setVehicles] = useState(null);
    useEffect(() => {
        if (targetOption !== "vehicle") return
        vehicleService.getSelectors()
            .then(response => setVehicles(response))
            .catch(error => console.error(error))
    }, [targetOption]);

    const [projectTasks, setProjectTasks] = useState(null);
    useEffect(() => {
        if (formValue?.project == null) return
        projectTaskService.search(formValue.project.id, {})
            .then(response => setProjectTasks(response.content))
            .catch(error => console.error(error))
    }, [formValue?.project]);

    const [tasks, setTasks] = useState(null);
    useEffect(() => {
        if (!open) return
        searchTasks("")
    }, [formValue.project, formValue.projectTask]);

    function searchTasks(text) {
        taskService.search({description: text, projectId: formValue.project?.id || '', projectTaskId: formValue.projectTask?.id || ''})
            .then(response => setTasks(response.content))
            .catch(error => console.error(error))
    }

    const [users, setUsers] = useState(null);
    useEffect(() => {
        if (users != null || targetOption !== 'user') return
        userService.getSelectors()
            .then(response => setUsers(response))
            .catch(error => console.error(error))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetOption]);

    const onChange = (e, newValue, targetName) => {
        if (targetName === 'project' && newValue === null)
            setFormValue((prev) => ({
                ...prev,
                projectTask: null,
                task: null
            }))
        else if (targetName === 'projectTask' && newValue === null)
            setFormValue((prev) => ({
                ...prev,
                task: null
            }))
        const {name, value} = e.target
        setFormValue((prev) => ({
            ...prev,
            [name || targetName]: newValue || value
        }))
    }

    const onTaskChange = (e) => {
        if (e == null) return
        const text = e.target.value;
        if (text) searchTasks(text)
    }

    useEffect(() => {
        localStorage.setItem("general-file-upload-form-values", JSON.stringify(formValue));
    }, [formValue]);

    const handleClose = () => {
        setOpen(false)
        setFiles([])
        setActiveStep(0)
        setTargetOption(null)
    }

    const handleSubmit = () => {
        setWaitingForServer(true);

        let url;
        switch (targetOption) {
            case 'project':
                url = `/projects/${formValue.project.id}/files`;
                break;
            case 'task':
                url = `/tasks/${formValue.task.id}/files`;
                break;
            case 'user':
                url = `/users/${formValue.user.id}/files`;
                break;
            case 'vehicle':
                url = `/vehicles/${formValue.vehicle.id}/files`;
                break;
            default:
                console.error('Invalid target option');
                setWaitingForServer(false);
                return;
        }

        uploadFiles(url, files, handleClose, sendAlertMessage, {description: formValue.description})
            .finally(() => setWaitingForServer(false));
    };


    const changeTargetOption = (id) => {
        setTargetOption(id)
        handleNext()
    }

    const handleRemoveFile = (index) => {
        setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
    };

    const handleNext = () => {
        console.log(formValue)
        if (activeStep === steps.length - 1) handleSubmit()
        else setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const isNextDisabled = () => {
        if (activeStep === 1) {
            return targetOption === 'project' ? formValue?.project == null :
                targetOption === 'task' ? (formValue?.task == null || formValue?.task === "") :
                    targetOption === 'user' ? formValue?.user == null :
                        false;
        } else if (activeStep === 2) {
            return files.length === 0;
        }
        return false;
    };

    return (
        <Dialog open={open} onClose={handleClose} PaperProps={{sx: {minWidth: "40vw"}}}>
            <DialogTitle sx={{background: "#1976d2", color: "white"}}>
                Dokumentumok Feltöltése
            </DialogTitle>
            <DialogContent>
                <Stepper activeStep={activeStep} sx={{margin: "16px 0px 8px 0px", overflow: "hidden"}}>
                    {steps.map((label, index) => {
                        const stepProps = {};
                        const labelProps = {};
                        return (
                            <Step key={index} {...stepProps}>
                                <StepLabel {...labelProps}>{label}</StepLabel>
                            </Step>
                        );
                    })}
                </Stepper>
                {activeStep === 0 &&
                    <Grid container columns={8} spacing={2}>
                        {targetOptions.map((item, index) => (
                            <Grid item xs={2} key={index}>
                                <Button color={targetOption === item.id ? 'success' : 'primary'} variant='contained'
                                        disabled={item.disabled}
                                        sx={{
                                            minWidth: "0px",
                                            padding: "5px",
                                            margin: "10px 10px",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            borderRadius: "15px"
                                        }}
                                        onClick={() => {
                                            changeTargetOption(item.id)
                                        }}>
                                    <div>{item.icon}</div>
                                    <div style={{marginTop: "-10px", fontSize: "20px"}}>{item.title}</div>
                                </Button>
                            </Grid>))}
                    </Grid>
                }
                {activeStep === 1 &&
                    <Grid container columns={10}>
                        {(targetOption === 'project' || targetOption === 'task') && <Grid item xs={10}>
                            <Autocomplete
                                value={formValue?.project || null}
                                margin="normal"
                                label="Projekt Neve"
                                name="project"
                                autoHighlight
                                autoSelect
                                onChange={(event, newValue) => onChange(event, newValue, "project")}
                                options={projects || []}
                                isOptionEqualToValue={(item, other) => item.id === other.id}
                                getOptionLabel={(project) => project.name}
                                style={{marginTop: "16px"}}
                                renderInput={(params) => (
                                    <TextField {...params} label="Projekt Neve"/>
                                )}
                            />
                        </Grid>}
                        {(targetOption === 'task') && <Grid item xs={10}>
                            <Autocomplete
                                value={formValue?.projectTask || null}
                                margin="normal"
                                label="Projekt Fejléc"
                                name="projectTask"
                                autoHighlight
                                autoSelect
                                onChange={(event, newValue) => onChange(event, newValue, "projectTask")}
                                options={projectTasks || []}
                                isOptionEqualToValue={(item, other) => item.id === other.id}
                                getOptionLabel={(projectTask) => projectTask.description}
                                style={{marginTop: "16px"}}
                                renderInput={(params) => (
                                    <TextField {...params} label="Projekt Fejléc"/>
                                )}
                            />
                        </Grid>}
                        {targetOption === 'task' && <Grid item xs={10}>
                            <Autocomplete
                                value={formValue?.task || null}
                                margin="normal"
                                label="Feladat"
                                name="task"
                                autoHighlight
                                autoSelect
                                onChange={(event, newValue) => onChange(event, newValue, "task")}
                                onInputChange={(event) => onTaskChange(event)}
                                options={tasks || []}
                                getOptionLabel={(task) => task.description + (task.projectName ? " (" + task.projectName + ")" : "")}
                                isOptionEqualToValue={(item, other) => item.id === other.id}
                                style={{marginTop: "16px"}}
                                renderInput={(params) => (
                                    <TextField {...params} label="Feladat" required/>
                                )}
                            />
                        </Grid>}
                        {targetOption === 'user' && <Grid item xs={10}>
                            <Autocomplete
                                value={formValue?.user || null}
                                margin="normal"
                                label="Felhasználó"
                                name="user"
                                autoHighlight
                                autoSelect
                                onChange={(event, newValue) => onChange(event, newValue, "user")}
                                options={users || []}
                                getOptionLabel={(user) => user.name}
                                isOptionEqualToValue={(item, other) => item.id === other.id}
                                style={{marginTop: "16px"}}
                                renderInput={(params) => (
                                    <TextField {...params} label="Felhasználó"/>
                                )}
                            />
                        </Grid>}
                        {targetOption === 'vehicle' && <Grid item xs={10}>
                            <Autocomplete
                                value={formValue?.vehicle || null}
                                margin="normal"
                                label="Jármű"
                                name="vehicle"
                                autoHighlight
                                autoSelect
                                onChange={(event, newValue) => onChange(event, newValue, "vehicle")}
                                options={vehicles || []}
                                getOptionLabel={(vehicle) => vehicle.licensePlate}
                                isOptionEqualToValue={(item, other) => item.id === other.id}
                                style={{marginTop: "16px"}}
                                renderInput={(params) => (
                                    <TextField {...params} label="Jármű"/>
                                )}
                            />
                        </Grid>}
                    </Grid>
                }
                {activeStep === 2 &&
                    <Grid container columns={10}>
                        <Grid item xs={8}>
                            <Table sx={{marginTop: "8px"}}>
                                <TableBody>
                                    {files.map((file, index) => (
                                        <React.Fragment key={index}>
                                            <TableRow>
                                                <TableCell sx={{textAlign: "left", padding: "0px 0px 0px 5px"}}>
                                                    {file.name}
                                                </TableCell>
                                                <TableCell sx={{textAlign: "left", padding: "0px"}}>
                                                    {(new Intl.NumberFormat().format((file.size / 1024).toFixed(2))).replace(',', '.')} KB
                                                </TableCell>
                                                <TableCell sx={{textAlign: "right", padding: "0px"}}>
                                                    <Button onClick={() => handleRemoveFile(index)} variant="text"
                                                            color='error'
                                                            sx={{marginLeft: "10px", padding: "5px", minWidth: "0px"}}>
                                                        <Close/>
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </Grid>
                        <Grid item xs={files?.length > 0 ? 2 : 10} textAlign="center">
                            <Button variant='contained' component="label"
                                    sx={{
                                        minWidth: "0px",
                                        padding: "5px",
                                        margin: "10px 10px",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        borderRadius: "15px",
                                        backgroundColor: "#efbf04",
                                        ':hover': {backgroundColor: '#d6ab04'}
                                    }}>
                                <div><Upload sx={{fontSize: "calc(6vw - 0px)", margin: "0px"}}/></div>
                                <div style={{marginTop: "-10px", fontSize: "20px"}}>Fájlok</div>
                                <VisuallyHiddenInput type="file" multiple onChange={handleFileChange}/>
                            </Button>
                        </Grid>
                    </Grid>}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="error" variant="outlined"
                        sx={{marginRight: activeStep === 0 ? "auto" : ""}}>
                    Mégse
                </Button>
                {activeStep > 0 &&
                    <Button onClick={handleBack} sx={{marginRight: "auto"}} color="primary" variant="contained">
                        Vissza
                    </Button>}
                {activeStep > 0 &&
                    <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        label="Megjegyzés"
                        name="description"
                        autoComplete="off"
                        onChange={onChange}
                        value={formValue?.description || ""}
                    />}
                {waitingForServer ? <CircularProgress sx={{marginRight: "20px"}}/> : <>
                    {targetOption &&
                        <Button onClick={handleNext} color="primary" variant="contained" disabled={isNextDisabled()}>
                            {activeStep === steps.length - 1 ? 'Feltöltés' : 'Következő'}
                        </Button>}
                </>}
            </DialogActions>
        </Dialog>
    )
})

export default GeneralFileUploadDialog;